import {
  createContext,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "@mantine/hooks";
import AuthService from "../services/AuthService";
import UserService from "../services/UserService";
import {
  TARGET_ORG_STORAGE_KEY,
  DATA_ORG_STORAGE_KEY,
  setSessionExpiredHandler,
} from "../services/apiClient";
import {
  MSAL_CONFIGURED,
  msalInstance,
  initMsal,
  getActiveAccount,
  getAllAccounts,
  ensureActiveAccount,
  clearMsalCache,
  LOGIN_SCOPES,
} from "../utils/msal";
import LoadingLayout from "../layouts/LoadingLayout";
import { OrgTypes } from "../utils/constants";
import { useLinkedAssetOwnersQuery } from "../hooks/useOrganisationQueries";
import { showErrorNotification } from "../utils/notifications";

export const AuthContext = createContext(null);

const SESSION_TYPE_KEY = "session_type";

// 'jwt' = SimpleJWT password login, 'msal' = Microsoft SSO; in localStorage alongside both credential stores so every tab resumes the same mode.
const persistSessionType = (mode) => {
  try {
    if (mode) localStorage.setItem(SESSION_TYPE_KEY, mode);
    else localStorage.removeItem(SESSION_TYPE_KEY);
    sessionStorage.removeItem(SESSION_TYPE_KEY);
  } catch {
    // Storage unavailable (private mode, quota); bootstrap re-derives the mode anyway.
  }
};

const readSessionType = () => {
  try {
    return localStorage.getItem(SESSION_TYPE_KEY);
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken"),
  );
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem("refreshToken"),
  );
  const [user, setUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [authInitializing, setAuthInitializing] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState(readSessionType); // 'jwt' | 'msal' | null
  const [msalLoginError, setMsalLoginError] = useState(null);
  // Microsoft-link outcome shown as a modal: { status, msEmail, vaparEmail, title, description }, copy from the server.
  const [msalLinkResult, setMsalLinkResult] = useState(null);

  const applyAuthMode = useCallback((mode) => {
    setAuthMode(mode);
    persistSessionType(mode);
  }, []);

  // Only one credential set may exist at a time; the request interceptor always prefers MSAL over a stored JWT.
  const clearJwtTokens = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setAccessToken(null);
    setRefreshToken(null);
  }, []);

  const clearMsalLoginError = useCallback(() => setMsalLoginError(null), []);
  const clearMsalLinkResult = useCallback(() => setMsalLinkResult(null), []);

  const [orgOptions, setOrgOptions] = useState([]);
  const [orgSelections, setOrgSelections] = useLocalStorage({
    key: "user-org-selections",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  // Asset owner a contractor is operating against, keyed by user.id; null for non-contractor working-in orgs.
  const [targetOrgSelections, setTargetOrgSelections] = useLocalStorage({
    key: "user-target-org-selections",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  const [appInitializing, setAppInitializing] = useState(true);
  const [orgStandard, setOrgStandard] = useState(null);

  const selectedOrgId = user ? orgSelections[user.id] : null;
  const targetOrgId = user ? targetOrgSelections[user.id] : null;

  const setSelectedOrgId = useCallback(
    (value) => {
      if (!user) return;
      setOrgSelections((prev) => ({
        ...prev,
        [user.id]: value,
      }));
    },
    [user, setOrgSelections],
  );

  const setTargetOrgId = useCallback(
    (value) => {
      if (!user) return;
      setTargetOrgSelections((prev) => ({
        ...prev,
        [user.id]: value,
      }));
    },
    [user, setTargetOrgSelections],
  );

  const workingInOrg = orgOptions.find(
    (o) => String(o.id) === String(selectedOrgId),
  );
  const workingInIsContractor =
    String(workingInOrg?.orgType) === String(OrgTypes.CONTRACTOR);
  const workingInIsAssetOwner =
    String(workingInOrg?.orgType) === String(OrgTypes.ASSET_OWNER);

  // For the primary org, /auth/me already holds the same rows the endpoint returns
  // (same serializer and link table), so seed the query with them. The list is then
  // instant, and the endpoint refetches in the background.
  const primaryLinkedAssetOwners = useMemo(() => {
    if (!organisation?.linkedOrganisations) return undefined;
    return organisation.linkedOrganisations
      .filter((o) => String(o.orgType) === String(OrgTypes.ASSET_OWNER))
      .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
  }, [organisation]);
  const workingInPrimaryOrg =
    !!workingInOrg && String(workingInOrg.id) === String(organisation?.id);

  // Target options for the working-in contractor (not part of orgOptions), always from
  // /organisations/<id>/asset-owners; empty for a non-contractor org.
  const {
    data: fetchedAssetOwners,
    isLoading: fetchedAssetOwnersLoading,
    isPlaceholderData: fetchedAssetOwnersStale,
    isError: fetchedAssetOwnersError,
  } = useLinkedAssetOwnersQuery(
    workingInIsContractor ? workingInOrg?.id : undefined,
    undefined,
    {
      initialData: workingInPrimaryOrg ? primaryLinkedAssetOwners : undefined,
    },
  );
  // Placeholder data is the previous contractor's list, so treat it as still loading.
  const linkedAssetOwnersLoading =
    workingInIsContractor &&
    (fetchedAssetOwnersLoading || fetchedAssetOwnersStale);
  const linkedAssetOwnersError =
    workingInIsContractor && fetchedAssetOwnersError;
  const linkedAssetOwners = useMemo(
    () =>
      workingInIsContractor && !fetchedAssetOwnersStale
        ? fetchedAssetOwners || []
        : [],
    [workingInIsContractor, fetchedAssetOwnersStale, fetchedAssetOwners],
  );

  // Org whose data we're VIEWING (X-Data-Org-Id / list ?organisation=); see ./auth-org-scoping.md.
  const targetIsValidAssetOwner =
    workingInIsContractor &&
    !!targetOrgId &&
    String(targetOrgId) !== String(selectedOrgId) &&
    (user?.isStaff ||
      linkedAssetOwners.some((o) => String(o.id) === String(targetOrgId)));
  const dataScopeOrgId = targetIsValidAssetOwner ? targetOrgId : selectedOrgId;

  // The resolved data-scope org: a validated target from linkedAssetOwners, else the working-in org.
  const dataScopeOrg = useMemo(() => {
    if (targetIsValidAssetOwner) {
      return (
        linkedAssetOwners.find((o) => String(o.id) === String(targetOrgId)) ||
        null
      );
    }
    return (
      orgOptions.find((o) => String(o.id) === String(selectedOrgId)) || null
    );
  }, [
    targetIsValidAssetOwner,
    targetOrgId,
    linkedAssetOwners,
    orgOptions,
    selectedOrgId,
  ]);

  // Whether the data-scope org is an Asset Owner, kept consistent with dataScopeOrgId.
  // The Assets table uses this for its empty state. Page and nav visibility use
  // workingInIsAssetOwner, so a contractor viewing a linked asset owner stays out.
  const dataScopeIsAssetOwner = targetIsValidAssetOwner
    ? true
    : workingInIsAssetOwner;

  // A non-staff user's linked-asset-owner Target can't be verified until the list loads.
  // Hold the app until then so no request fires with the fallback (working-in) scope.
  const dataScopeResolving =
    workingInIsContractor &&
    !user?.isStaff &&
    !!targetOrgId &&
    String(targetOrgId) !== String(selectedOrgId) &&
    linkedAssetOwnersLoading;

  // Correct a stale or no-longer-linked persisted Target BEFORE any org-scoped request fires, skipping load/error states.
  useEffect(() => {
    if (!user || !workingInIsContractor) return;
    if (linkedAssetOwnersLoading || linkedAssetOwnersError) return;
    const isOwnTarget = String(targetOrgId) === String(selectedOrgId);
    const isLinkedTarget = linkedAssetOwners.some(
      (o) => String(o.id) === String(targetOrgId),
    );
    if (!targetOrgId || (!isOwnTarget && !isLinkedTarget)) {
      setTargetOrgId(String(selectedOrgId));
    }
  }, [
    user,
    workingInIsContractor,
    selectedOrgId,
    targetOrgId,
    linkedAssetOwners,
    linkedAssetOwnersLoading,
    linkedAssetOwnersError,
    setTargetOrgId,
  ]);

  // Headers are read from localStorage when a request starts. This has to run
  // in a layout effect: dashboard queries refetch in a child useEffect, which
  // runs after every layout effect and would otherwise send the previous org.
  useLayoutEffect(() => {
    if (selectedOrgId) {
      localStorage.setItem(TARGET_ORG_STORAGE_KEY, String(selectedOrgId));
    } else {
      localStorage.removeItem(TARGET_ORG_STORAGE_KEY);
    }

    if (dataScopeOrgId && String(dataScopeOrgId) !== String(selectedOrgId)) {
      localStorage.setItem(DATA_ORG_STORAGE_KEY, String(dataScopeOrgId));
    } else {
      localStorage.removeItem(DATA_ORG_STORAGE_KEY);
    }
  }, [selectedOrgId, dataScopeOrgId]);

  const initializeSelectedOrgId = useCallback(() => {
    if (!user || orgOptions.length === 0) return;

    const selectedOrgIdIsValid =
      selectedOrgId &&
      orgOptions.find((org) => String(org.id) === String(selectedOrgId));

    if (!selectedOrgIdIsValid) {
      setSelectedOrgId(String(orgOptions[0].id));
    }
  }, [user, orgOptions, selectedOrgId, setSelectedOrgId]);

  const login = useCallback(
    async (email, password) => {
      setLoggingIn(true);
      try {
        const { accessToken, refreshToken, user, organisation } =
          await AuthService.login({ email, password });

        // A leftover Microsoft account would out-rank this JWT in the request interceptor.
        await clearMsalCache();

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setUser(user);
        setOrganisation(organisation);
        setIsAuthenticated(true);
        applyAuthMode("jwt");
      } finally {
        setLoggingIn(false);
      }
    },
    [applyAuthMode],
  );

  const loginWithMicrosoft = useCallback(async (intent = "login") => {
    if (!MSAL_CONFIGURED || !msalInstance) {
      throw new Error("Microsoft sign-in is not configured.");
    }
    await initMsal();
    // `state` round-trips through the MS authorize endpoint so we can tell a fresh sign-in from an account link.
    // Without prompt, Microsoft reuses its own session cookie — signing in as a different user would
    // need a full logout first, and linking would silently bind whichever account is already signed in.
    await msalInstance.loginRedirect({
      scopes: LOGIN_SCOPES,
      state: intent,
      prompt: "select_account",
    });
  }, []);

  // oid is only written by the link endpoint, so an SSO session reports none while plainly signed in with Microsoft.
  const msAccountLinked = !!user && (!!user.oid || authMode === "msal");

  // Shared by the banner and Account Settings; the link flow returns to the existing JWT session, so it needs one.
  const canLinkMsAccount =
    MSAL_CONFIGURED && !!user && authMode === "jwt" && !msAccountLinked;

  const linkMicrosoftAccount = useCallback(() => {
    // With no JWT to return to, completeMsLink would clear the user's only credential.
    if (authMode !== "jwt") {
      return Promise.reject(
        new Error(
          "You are already signed in with Microsoft, so there is nothing to link.",
        ),
      );
    }
    return loginWithMicrosoft("link");
  }, [authMode, loginWithMicrosoft]);

  const logout = useCallback(async () => {
    clearJwtTokens();
    localStorage.removeItem("assets-standard");
    localStorage.removeItem(TARGET_ORG_STORAGE_KEY);
    localStorage.removeItem(DATA_ORG_STORAGE_KEY);
    // Reset the banner dismissal so the next user on this tab re-evaluates it for their own account.
    sessionStorage.removeItem("link_ms_banner_dismissed");
    setUser(null);
    setOrganisation(null);
    setIsAuthenticated(false);
    setOrgOptions([]);
    setAppInitializing(true);
    applyAuthMode(null);
    setMsalLinkResult(null);
    setMsalLoginError(null);

    // Any cached account, active or not, would silently sign the next tab back in.
    if (MSAL_CONFIGURED && msalInstance) {
      await initMsal();
      const account = getActiveAccount() || getAllAccounts()[0] || null;
      if (account) {
        // logoutRedirect navigates away; nothing after this runs.
        await msalInstance.logoutRedirect({ account });
        return;
      }
    }

    navigate("/login");
  }, [navigate, applyAuthMode, clearJwtTokens]);

  // Bootstrap auth once on mount: MSAL redirect callback, then MSAL or SimpleJWT hydration.
  useEffect(() => {
    let cancelled = false;

    const hydrateMsalSession = async () => {
      try {
        const { user, organisation } = await AuthService.me();
        if (cancelled) return false;
        // SSO owns the session from here; a stale JWT pair would linger as a second credential.
        clearJwtTokens();
        setUser(user);
        setOrganisation(organisation);
        setIsAuthenticated(true);
        applyAuthMode("msal");
        setMsalLoginError(null);
        return true;
      } catch (error) {
        // CREATE_NEW_USERS=False: a valid MS bearer for an unknown VAPAR user 401s, so surface that distinctly.
        if (error?.response?.status === 401) {
          setMsalLoginError(
            "No VAPAR account is linked to this Microsoft identity. Contact your VAPAR admin.",
          );
        } else {
          console.error("Failed to hydrate MSAL session:", error);
        }
        await clearMsalCache();
        return false;
      }
    };

    // Rehydrate a JWT session via /auth/me, refreshing the access token once on a 401.
    const verifySimpleJwtSession = async () => {
      // A missing access token is still a live session if the refresh token survives.
      if (!accessToken && !refreshToken) return false;

      const hydrate = async () => {
        const { user, organisation } = await AuthService.me();
        if (cancelled) return false;
        setUser(user);
        setOrganisation(organisation);
        setIsAuthenticated(true);
        applyAuthMode("jwt");
        // The 401 interceptor may have refreshed mid-flight; resync from storage.
        setAccessToken(localStorage.getItem("accessToken"));
        return true;
      };

      const refreshAndHydrate = async () => {
        if (!refreshToken) return false;
        try {
          const newAccess = await AuthService.refreshToken(refreshToken);
          if (cancelled) return false;
          localStorage.setItem("accessToken", newAccess);
          setAccessToken(newAccess);
          return await hydrate();
        } catch {
          return false;
        }
      };

      if (!accessToken) return refreshAndHydrate();

      try {
        return await hydrate();
      } catch (error) {
        if (error?.response?.status !== 401) return false;
        return refreshAndHydrate();
      }
    };

    // Link intent: use the MS token only to PATCH the link endpoint, staying on the existing JWT session.
    const completeMsLink = async (msAccessToken, msAccountEmail) => {
      // Clearing the active account forces the request interceptor onto the JWT rather than an MSAL bearer.
      try {
        msalInstance.setActiveAccount(null);
      } catch {
        // ignore
      }

      // /auth/me on the JWT gives us the canonical VAPAR email to send to the link endpoint.
      let userEmail = null;
      try {
        const { user, organisation } = await AuthService.me();
        if (cancelled) return;
        setUser(user);
        setOrganisation(organisation);
        setIsAuthenticated(true);
        applyAuthMode("jwt");
        userEmail = user.email;
      } catch (error) {
        console.error("Could not load JWT session before linking:", error);
        // Leave no MS account behind, or the next tab would boot into an SSO session the user never asked for.
        await clearMsalCache();
        showErrorNotification({
          title: "Link Microsoft account",
          userMessage:
            "Your VAPAR session has expired. Please sign in again and try linking from the banner.",
        });
        return;
      }

      let linked = false;
      let linkResponse = null;
      try {
        linkResponse = await AuthService.linkMsAccount({
          msAccessToken,
          email: userEmail,
        });
        linked = true;
      } catch (error) {
        // Link failures show in the result modal using the server's display copy, not a transient toast.
        const data = error?.response?.data || {};
        setMsalLinkResult({
          status: "error",
          msEmail: msAccountEmail || null,
          vaparEmail: userEmail,
          title: data.title || null,
          description:
            data.description || data.error || error?.userMessage || null,
        });
      } finally {
        // Either way clear MSAL so the app doesn't treat this as an SSO session; the user keeps their JWT.
        await clearMsalCache();
      }

      if (!linked) return;

      // Re-hydrate so user.oid is populated and the banner disappears.
      try {
        const { user, organisation } = await AuthService.me();
        if (cancelled) return;
        setUser(user);
        setOrganisation(organisation);
        setMsalLinkResult({
          status: "success",
          msEmail: msAccountEmail || null,
          vaparEmail: user.email,
          title: linkResponse?.title || null,
          description: linkResponse?.description || null,
        });
      } catch (error) {
        console.error("Post-link hydration failed:", error);
      }
    };

    const bootstrap = async () => {
      try {
        if (MSAL_CONFIGURED && msalInstance) {
          await initMsal();

          const redirectResult = await msalInstance.handleRedirectPromise();

          if (redirectResult?.state === "link" && redirectResult.accessToken) {
            // Link flow: do NOT setActiveAccount; completeMsLink does the PATCH and clears MSAL when finished.
            await completeMsLink(
              redirectResult.accessToken,
              redirectResult.account?.username,
            );
            return;
          }

          if (redirectResult?.account) {
            msalInstance.setActiveAccount(redirectResult.account);
          }

          // MSAL is tried first because the request interceptor prefers an MS bearer over a stored JWT.
          if (ensureActiveAccount()) {
            const ok = await hydrateMsalSession();
            if (ok) return;
          }
        }

        const ok = await verifySimpleJwtSession();
        if (!ok) {
          // No session - clear any stale tokens and let the user log in.
          clearJwtTokens();
        }
      } finally {
        if (!cancelled) setAuthInitializing(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setSessionExpiredHandler(logout), [logout]);

  useEffect(() => {
    const fetchOrgOptions = async () => {
      if (authInitializing) return;

      if (!isAuthenticated) {
        setAppInitializing(false);
        return;
      }

      setAppInitializing(true);
      try {
        const options = await UserService.getCurrentUserOrganisations();
        setOrgOptions(options);
      } catch (error) {
        console.error("Error loading organisations:", error);
      } finally {
        setAppInitializing(false);
      }
    };

    fetchOrgOptions();
  }, [isAuthenticated, authInitializing]);

  useEffect(() => {
    initializeSelectedOrgId();
  }, [orgOptions, initializeSelectedOrgId]);

  // Track the data-scope org's standard (not the primary org's), consumed by AssetsContext.
  useEffect(() => {
    setOrgStandard(dataScopeOrg?.standardKey || null);
  }, [dataScopeOrg]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        user,
        organisation,
        login,
        loginWithMicrosoft,
        linkMicrosoftAccount,
        msAccountLinked,
        canLinkMsAccount,
        logout,
        isAuthenticated,
        authInitializing,
        authMode,
        msalLoginError,
        clearMsalLoginError,
        msalLinkResult,
        clearMsalLinkResult,
        loggingIn,
        orgOptions,
        selectedOrgId,
        setSelectedOrgId,
        workingInOrg,
        workingInIsAssetOwner,
        targetOrgId,
        setTargetOrgId,
        linkedAssetOwners,
        linkedAssetOwnersLoading,
        linkedAssetOwnersError,
        dataScopeOrgId,
        dataScopeOrg,
        dataScopeIsAssetOwner,
        appInitializing: appInitializing || dataScopeResolving,
        orgStandard,
      }}
    >
      {authInitializing ? <LoadingLayout /> : children}
    </AuthContext.Provider>
  );
}
